import * as React from "react";
import { Link } from "react-router-dom";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { ArrowLeft, Mail, KeyRound, Lock, CheckCircle } from "lucide-react";
import toast from "react-hot-toast";
import {
  useForgotPassword,
  useVerifyResetOtp,
  useResetPassword,
} from "../hooks/useLogin";
import {
  forgotPasswordSchema,
  verifyOtpSchema,
  resetPasswordSchema,
  type ForgotPasswordFormData,
  type VerifyOtpFormData,
  type ResetPasswordFormData,
} from "../schemas/auth.schema";
import { ROUTES } from "@/constants/routes";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";

type Step = "email" | "otp" | "password" | "done";

export function ForgotPasswordPage() {
  const [step, setStep] = React.useState<Step>("email");
  const [email, setEmail] = React.useState("");
  const [resetToken, setResetToken] = React.useState("");

  const { mutate: forgotPassword, isPending: isSendingOtp } = useForgotPassword();
  const { mutate: verifyOtp, isPending: isVerifyingOtp } = useVerifyResetOtp();
  const { mutate: resetPassword, isPending: isResettingPassword } = useResetPassword();

  const emailForm = useForm<ForgotPasswordFormData>({
    resolver: zodResolver(forgotPasswordSchema),
    defaultValues: { email: "" },
  });

  const otpForm = useForm<VerifyOtpFormData>({
    resolver: zodResolver(verifyOtpSchema),
    defaultValues: { otp: "" },
  });

  const passwordForm = useForm<ResetPasswordFormData>({
    resolver: zodResolver(resetPasswordSchema),
    defaultValues: { password: "", confirmPassword: "" },
  });

  const onEmailSubmit = (data: ForgotPasswordFormData) => {
    forgotPassword(data.email, {
      onSuccess: () => {
        setEmail(data.email);
        setStep("otp");
        toast.success("OTP sent to your email");
      },
      onError: (error: Error) => {
        toast.error(error.message || "Failed to send OTP");
      },
    });
  };

  const onOtpSubmit = (data: VerifyOtpFormData) => {
    verifyOtp(
      { email, otp: data.otp },
      {
        onSuccess: (response) => {
          setResetToken(response.data.reset_token);
          setStep("password");
        },
        onError: (error: Error) => {
          toast.error(error.message || "Invalid or expired OTP");
        },
      }
    );
  };

  const onPasswordSubmit = (data: ResetPasswordFormData) => {
    resetPassword(
      { token: resetToken, password: data.password },
      {
        onSuccess: () => {
          setStep("done");
        },
        onError: (error: Error) => {
          toast.error(error.message || "Failed to reset password");
        },
      }
    );
  };

  if (step === "done") {
    return (
      <div className="space-y-6 text-center">
        <div className="mx-auto flex h-12 w-12 items-center justify-center rounded-full bg-green-100">
          <CheckCircle className="h-6 w-6 text-green-600" />
        </div>
        <div>
          <h2 className="text-lg font-semibold">Password reset successful</h2>
          <p className="mt-1 text-sm text-muted-foreground">
            Your password has been updated. You can now log in with your new password.
          </p>
        </div>
        <Link to={ROUTES.LOGIN}>
          <Button className="w-full">Back to login</Button>
        </Link>
      </div>
    );
  }

  if (step === "password") {
    return (
      <div className="space-y-6">
        <div className="text-center">
          <h2 className="text-lg font-semibold">Set new password</h2>
          <p className="mt-1 text-sm text-muted-foreground">
            Choose a strong password for your account
          </p>
        </div>

        <form
          onSubmit={passwordForm.handleSubmit(onPasswordSubmit)}
          className="space-y-4"
        >
          <div className="space-y-2">
            <Label htmlFor="password" required>
              New password
            </Label>
            <Input
              id="password"
              type="password"
              placeholder="Min. 8 characters"
              autoComplete="new-password"
              leftIcon={<Lock className="h-4 w-4" />}
              error={passwordForm.formState.errors.password?.message}
              {...passwordForm.register("password")}
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="confirmPassword" required>
              Confirm password
            </Label>
            <Input
              id="confirmPassword"
              type="password"
              placeholder="Re-enter your password"
              autoComplete="new-password"
              leftIcon={<Lock className="h-4 w-4" />}
              error={passwordForm.formState.errors.confirmPassword?.message}
              {...passwordForm.register("confirmPassword")}
            />
          </div>

          <Button type="submit" className="w-full" isLoading={isResettingPassword}>
            Reset password
          </Button>
        </form>
      </div>
    );
  }

  if (step === "otp") {
    return (
      <div className="space-y-6">
        <div className="text-center">
          <h2 className="text-lg font-semibold">Enter OTP</h2>
          <p className="mt-1 text-sm text-muted-foreground">
            We sent a 6-digit code to{" "}
            <span className="font-medium text-foreground">{email}</span>
          </p>
        </div>

        <form onSubmit={otpForm.handleSubmit(onOtpSubmit)} className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="otp" required>
              One-time password
            </Label>
            <Input
              id="otp"
              type="text"
              inputMode="numeric"
              placeholder="000000"
              maxLength={6}
              autoComplete="one-time-code"
              leftIcon={<KeyRound className="h-4 w-4" />}
              error={otpForm.formState.errors.otp?.message}
              {...otpForm.register("otp")}
            />
          </div>

          <Button type="submit" className="w-full" isLoading={isVerifyingOtp}>
            Verify OTP
          </Button>
        </form>

        <div className="flex flex-col gap-2">
          <Button
            variant="outline"
            className="w-full"
            disabled={isSendingOtp}
            onClick={() => {
              forgotPassword(email, {
                onSuccess: () => toast.success("OTP resent to your email"),
                onError: (error: Error) =>
                  toast.error(error.message || "Failed to resend OTP"),
              });
            }}
          >
            {isSendingOtp ? "Sending…" : "Resend OTP"}
          </Button>
          <Button
            variant="ghost"
            className="w-full"
            onClick={() => {
              setStep("email");
              otpForm.reset();
            }}
          >
            <ArrowLeft className="mr-2 h-4 w-4" />
            Change email
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="text-center">
        <h2 className="text-lg font-semibold">Forgot your password?</h2>
        <p className="mt-1 text-sm text-muted-foreground">
          Enter your admin email and we'll send you an OTP
        </p>
      </div>

      <form onSubmit={emailForm.handleSubmit(onEmailSubmit)} className="space-y-4">
        <div className="space-y-2">
          <Label htmlFor="email" required>
            Email address
          </Label>
          <Input
            id="email"
            type="email"
            placeholder="admin@bookfleet.in"
            autoComplete="email"
            leftIcon={<Mail className="h-4 w-4" />}
            error={emailForm.formState.errors.email?.message}
            {...emailForm.register("email")}
          />
        </div>

        <Button type="submit" className="w-full" isLoading={isSendingOtp}>
          Send OTP
        </Button>
      </form>

      <Link to={ROUTES.LOGIN}>
        <Button variant="ghost" className="w-full">
          <ArrowLeft className="mr-2 h-4 w-4" />
          Back to login
        </Button>
      </Link>
    </div>
  );
}
